import { CSSProperties } from 'react';

/**
 * Concrete hex values for Recharts (SVG attributes can't resolve CSS variables).
 * Status slice colors were validated with the dataviz palette validator against
 * both surfaces: lightness band, ≥3:1 contrast, worst adjacent CVD ΔE 15+.
 * The slice ORDER below is the CVD-safety mechanism — indigo/purple/blue are
 * never adjacent, nor are amber/orange. The gray "pending" slot is intentionally
 * low-chroma (semantic gray) and is mitigated by the legend, slice gaps, and
 * tooltips. Statuses not in the list sort last.
 */
export const STATUS_SLICE_ORDER = [
  'pending',
  'confirmed',
  'processing',
  'ready_to_ship',
  'formulated',
  'shipped',
  'delivered',
  'refunded',
  'dispensing',
  'completed',
  'cancelled',
];

export function sliceRank(status: string): number {
  const i = STATUS_SLICE_ORDER.indexOf(status);
  return i === -1 ? STATUS_SLICE_ORDER.length : i;
}

export interface ChartTheme {
  gold: string;
  grid: string;
  axis: string;
  surface: string;
  tooltip: CSSProperties;
  status: Record<string, string>;
}

export function chartTheme(dark: boolean): ChartTheme {
  return {
    gold: dark ? '#c9a96e' : '#b8943f',
    grid: dark ? '#2a2a3a' : '#e2e2ec',
    axis: dark ? '#8a8a9a' : '#6b6b80',
    surface: dark ? '#1a1a24' : '#ffffff',
    tooltip: {
      backgroundColor: dark ? '#1a1a24' : '#ffffff',
      border: `1px solid ${dark ? '#2a2a3a' : '#e2e2ec'}`,
      borderRadius: 12,
      color: dark ? '#e8e8f0' : '#1a1a2e',
      fontSize: 12,
      boxShadow: '0 4px 12px rgb(0 0 0 / 0.15)',
    },
    status: dark
      ? {
          pending: '#64748b',
          confirmed: '#3b82f6',
          processing: '#b45309',
          ready_to_ship: '#06b6d4',
          formulated: '#f97316',
          shipped: '#6366f1',
          delivered: '#16a34a',
          refunded: '#ea580c',
          dispensing: '#a855f7',
          completed: '#0d9488',
          cancelled: '#ef4444',
        }
      : {
          pending: '#64748b',
          confirmed: '#2563eb',
          processing: '#b45309',
          ready_to_ship: '#0891b2',
          formulated: '#ea580c',
          shipped: '#4f46e5',
          delivered: '#15803d',
          refunded: '#c2410c',
          dispensing: '#9333ea',
          completed: '#0d9488',
          cancelled: '#dc2626',
        },
  };
}
